import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { isArray, isDate, isObjectLike } from 'lodash'
import moment from 'moment-timezone'
import { Observable } from 'rxjs'

import { environment } from '../../../../../environments'
import { RestResponse } from './rest-response'

export interface HttpRequestOptions {
  headers?: HttpHeaders
  params?: HttpParams
  reportProgress?: boolean
  responseType?: 'json'
  withCredentials?: boolean
  body?: any
  allowSensitiveQueryParams?: boolean
}

export interface ParamsObject {
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | null
    | Date
    | moment.Moment
    | (string | number)[]
}

@Injectable({
  providedIn: 'root',
})
export class RestService {
  constructor(protected http: HttpClient) {}

  convertToParams(input?: ParamsObject): HttpParams {
    const params: { [key: string]: string } = {}

    if (input && isObjectLike(input)) {
      for (const key of Object.keys(input)) {
        const value = input[key]
        if (isArray(value)) {
          if (value.length > 0) {
            params[key] = value.join(',')
          }
        } else if (typeof value === 'boolean') {
          params[key] = value ? 'true' : 'false'
        } else if (typeof value === 'string' && value.length > 0) {
          params[key] = value
        } else if (isDate(value) || moment.isMoment(value)) {
          params[key] = moment(value).format('YYYY-MM-DD')
        } else if (value != null) {
          params[key] = String(value)
        }
      }
    }

    return new HttpParams({ fromObject: params })
  }

  // retrieve something
  get<T>(
    route: string,
    params?: HttpParams,
    options: HttpRequestOptions = {},
  ): Observable<RestResponse<T>> {
    const finalUrl = this.buildFinalUrlFromRoute(route)
    options.headers = this.getDefaultHeaders(options.headers)
    options.params = params
    options = this.getDefaultOptions(options)
    this.assertSafeQueryParams(options.params, options.allowSensitiveQueryParams)
    return this.http.get<RestResponse<T>>(finalUrl, options)
  }

  getCSV(
    route: string,
    params?: HttpParams,
    options: HttpRequestOptions = {},
  ): Observable<string> {
    const finalUrl = this.buildFinalUrlFromRoute(route)
    options.headers = this.getDefaultHeaders(options.headers)
    options.params = params
    ;(options as any).responseType = 'text'
    return this.http.get<string>(finalUrl, options)
  }

  // create something
  post<T>(
    route: string,
    body: any,
    options: HttpRequestOptions = {},
  ): Observable<RestResponse<T>> {
    const finalUrl = this.buildFinalUrlFromRoute(route)
    options.headers = this.getDefaultHeaders(options.headers)
    options = this.getDefaultOptions(options)
    return this.http.post<RestResponse<T>>(finalUrl, body, options)
  }

  // update something via replacing all of its data
  put<T>(
    route: string,
    body: any,
    options: HttpRequestOptions = {},
  ): Observable<RestResponse<T>> {
    const finalUrl = this.buildFinalUrlFromRoute(route)
    options.headers = this.getDefaultHeaders(options.headers)
    options = this.getDefaultOptions(options)
    return this.http.put<RestResponse<T>>(finalUrl, body, options)
  }

  // partial update something
  patch<T>(
    route: string,
    body: any,
    options: HttpRequestOptions = {},
  ): Observable<RestResponse<T>> {
    const finalUrl = this.buildFinalUrlFromRoute(route)
    options.headers = this.getDefaultHeaders(options.headers)
    options = this.getDefaultOptions(options)
    return this.http.patch<RestResponse<T>>(finalUrl, body, options)
  }

  // delete something
  delete<T>(
    route: string,
    options: HttpRequestOptions = {},
  ): Observable<RestResponse<T>> {
    const finalUrl = this.buildFinalUrlFromRoute(route)
    options.headers = this.getDefaultHeaders(options.headers)
    options = this.getDefaultOptions(options)
    return this.http.delete<RestResponse<T>>(finalUrl, options)
  }

  getApiUrl(route: string = '') {
    return this.buildFinalUrlFromRoute(route)
  }

  getUrl(route: string = '') {
    const validatedRoute = this.validateRoute(route)
    return this.resolveUrl(this.getBaseUrl(), validatedRoute)
  }

  protected getDefaultHeaders(headers?: HttpHeaders): HttpHeaders {
    if (!headers) {
      headers = new HttpHeaders()
    }

    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json')
    }

    return headers
  }

  protected getDefaultOptions(options: HttpRequestOptions): HttpRequestOptions {
    return Object.assign(
      {
        responseType: 'json',
      },
      options,
    )
  }

  private buildFinalUrlFromRoute(route: string): string {
    const baseUrlRaw = (environment.api ?? '').trim().replace(/\/+$/, '')
    if (!baseUrlRaw) {
      throw new Error('Base API URL is empty')
    }
    const base = new URL(baseUrlRaw)
    if (!['http:', 'https:'].includes(base.protocol)) {
      throw new Error('Unsupported protocol in base API URL')
    }
    if (
      base.protocol === 'http:' &&
      environment.production &&
      !this.isLocalhost(base.hostname)
    ) {
      throw new Error('Insecure protocol is not allowed in production')
    }
    if (base.username || base.password) {
      throw new Error('Credentials are not allowed in base API URL')
    }

    const path = this.validateRoute(route)
    const finalUrl = `${baseUrlRaw}${path}`
    const parsed = new URL(finalUrl)
    if (parsed.origin !== base.origin) {
      throw new Error('Resolved URL is outside the allowed base URL')
    }

    return finalUrl
  }

  private validateRoute(route: string): string {
    if (!route) return '/'

    const trimmed = route.trim()

    if (!trimmed.startsWith('/')) {
      throw new Error('Route must start with "/"')
    }
    if (trimmed.startsWith('//')) {
      throw new Error('Protocol-relative URLs are not allowed')
    }
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
      throw new Error('URL schemes are not allowed')
    }
    if (trimmed.includes('..')) {
      throw new Error('Route cannot contain ".."')
    }
    if (trimmed.includes('\\') || /[\u0000-\u001F\u007F]/.test(trimmed)) {
      throw new Error('Invalid characters in route')
    }

    return trimmed
  }

  private getBaseUrl() {
    return window.location.origin
  }

  private resolveUrl(from: string, to: string) {
    const resolvedUrl = new URL(to, new URL(from, 'resolve://'))
    if (resolvedUrl.protocol === 'resolve:') {
      const { pathname, search, hash } = resolvedUrl
      return pathname + search + hash
    }
    return resolvedUrl.toString()
  }

  private assertSafeQueryParams(
    params?: HttpParams,
    allowSensitiveQueryParams?: boolean,
  ) {
    if (!params || allowSensitiveQueryParams || !(params instanceof HttpParams)) return

    const sensitiveKeyPattern =
      /^(pass(word)?|token|secret|ssn|social|dob|birth|email|phone|mrn|patient|member|insurance|icd|diagnosis|phi|pii|api[_-]?key|auth(orization)?|cookie|session|credit|card|cvv|pan)$/i

    const keys = params.keys()
    for (const key of keys) {
      if (sensitiveKeyPattern.test(key)) {
        throw new Error(
          `Sensitive query parameter "${key}" is not allowed in GET requests`,
        )
      }
    }
  }

  private isLocalhost(hostname: string): boolean {
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '::1'
    )
  }
}
